import { type ReactNode, useMemo } from 'react';
import { formatMoney } from '@repo/design-system-kit';
import { useIntl } from 'react-intl';
import { Link } from '@repo/poly-hooks/components';
import type { InvoiceStatus } from 'receivable-invoices-js/react/api/models/receivable-invoice';
import type { QuoteStatus, QuoteVersion } from 'receivable-invoices-js/react/api/models/quote';
import { Dropdown } from '../dropdown/dropdown';
import styles from './card.module.css';
import { StatusAndDate } from './status-and-date/status-and-date';

export interface CardProps {
  documentId: string;
  documentNumber: string;
  amount: string;
  date: string; // YYYY-MM-DD
  status: InvoiceStatus | QuoteStatus;
  currency: string;
  isQuote?: boolean;
  quoteVersions?: QuoteVersion[];
}

export function Card({
  documentId,
  documentNumber,
  amount,
  currency,
  date,
  status,
  isQuote = false,
  quoteVersions,
}: CardProps): ReactNode {
  const { locale, formatMessage } = useIntl();

  const title = isQuote
    ? formatMessage({ id: 'quote.preview.quote' }, { quoteNumber: documentNumber })
    : formatMessage({ id: 'client-portal.invoice-number' }, { invoiceNumber: documentNumber });

  const documentAmount = useMemo(() => {
    return formatMoney({ amount, locale, currency });
  }, [amount, currency, locale]);

  const href = isQuote ? `/quotes/${documentId}` : `/invoices/${documentId}`;

  return (
    <div className={styles.container} data-test-document-id={documentNumber}>
      <Link className={styles.information} data-test-document-link={documentNumber} to={href}>
        <div className={styles.titleContainer}>
          <p className="body-1" data-test-card-title>
            {title}
          </p>
          <div className={styles.dateContainer}>
            <StatusAndDate date={date} isQuote={isQuote} status={status} />
          </div>
        </div>
        <div className={styles.amount}>
          <p className="body-1" data-test-card-amount>
            {documentAmount}
          </p>
        </div>
      </Link>
      {isQuote && quoteVersions && quoteVersions.length > 1 ? (
        <Dropdown history={quoteVersions} />
      ) : null}
    </div>
  );
}
